import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Bug } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface PromptRow {
  id: string;
  created_at: string;
  variant_id: string | null;
  phase: string;
  model: string | null;
  input_messages: unknown;
  output_text: string | null;
  output_parsed: unknown;
  tokens_in: number | null;
  tokens_out: number | null;
  latency_ms: number | null;
  error: string | null;
}

const PHASE_ORDER: Record<string, number> = {
  style_brief: 0,
  spatial_analysis: 1,
  furnishing_strategy: 2,
  joint_variations: 3,
  image_prompt: 4,
};

const PHASE_LABELS: Record<string, string> = {
  style_brief: "Style brief (from inspiration)",
  spatial_analysis: "Step 1 — Spatial analysis",
  furnishing_strategy: "Step 2 — Furnishing strategy",
  joint_variations: "Step 3 — Image instructions (single design)",
  image_prompt: "Image generation prompt",
  image_render: "Image render (failed)",
  furniture_identify: "Furniture identification",
};

type Attempt = { attempt: number; model: string; status: number | string; ms: number };
const attemptsOf = (r: PromptRow): Attempt[] =>
  ((r.output_parsed as { attempts?: Attempt[] } | null)?.attempts ?? []);
const isSkipped = (r: PromptRow) => !!(r.output_parsed as { skipped?: boolean } | null)?.skipped;

function formatMs(ms: number | null) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function inputAsText(input: unknown): string {
  try {
    return typeof input === "string" ? input : JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

export default function DesignDebugPanel({ generationId }: { generationId: string }) {
  const [rows, setRows] = useState<PromptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("design_prompts")
        .select(
          "id, created_at, variant_id, phase, model, input_messages, output_text, output_parsed, tokens_in, tokens_out, latency_ms, error",
        )
        .eq("generation_id", generationId)
        .order("created_at", { ascending: true });
      if (!cancelled) {
        if (!error && data) setRows(data as PromptRow[]);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [generationId]);

  const sorted = [...rows].sort((a, b) => {
    const pa = PHASE_ORDER[a.phase] ?? 99;
    const pb = PHASE_ORDER[b.phase] ?? 99;
    if (pa !== pb) return pa - pb;
    return (a.variant_id ?? "").localeCompare(b.variant_id ?? "");
  });

  return (
    <div className="mt-10 border border-dashed border-foreground/30 bg-muted/30">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground"
      >
        <span className="flex items-center gap-2">
          <Bug className="w-4 h-4" />
          Debug — LLM prompt chain ({rows.length})
        </span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <p className="text-xs text-muted-foreground">
            Generation ID: <code className="font-mono">{generationId}</code>
          </p>
          {loading ? (
            <p className="text-xs text-muted-foreground">Loading prompts…</p>
          ) : sorted.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No prompts logged for this generation (older design, or planning was skipped).
            </p>
          ) : (
            sorted.map((r) => {
              const key = r.id;
              const isOpen = !!expanded[key];
              return (
                <div key={key} className="border border-border bg-background">
                  <button
                    type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? (
                        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                      )}
                      <span className="text-xs font-medium text-foreground">
                        {PHASE_LABELS[r.phase] ?? r.phase}
                      </span>
                      {r.variant_id && (
                        <span className="text-[10px] tracking-normal text-muted-foreground bg-muted px-1.5 py-0.5">
                          {r.variant_id}
                        </span>
                      )}
                      {isSkipped(r) ? (
                        <span className="text-[10px] tracking-normal text-muted-foreground bg-muted px-1.5 py-0.5">
                          skipped
                        </span>
                      ) : r.error ? (
                        <span className="text-[10px] tracking-normal text-destructive bg-destructive/10 px-1.5 py-0.5">
                          error
                        </span>
                      ) : null}
                      {attemptsOf(r).length > 1 && (
                        <span className="text-[10px] tracking-normal text-muted-foreground bg-muted px-1.5 py-0.5">
                          {attemptsOf(r).length} tries
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
                      <span>{formatMs(r.latency_ms)}</span>
                      {(r.tokens_in != null || r.tokens_out != null) && (
                        <span>
                          {r.tokens_in ?? "?"}→{r.tokens_out ?? "?"} tok
                        </span>
                      )}
                      {r.model && <span className="hidden sm:inline">{r.model}</span>}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border px-3 py-3 space-y-3">
                      {attemptsOf(r).length > 0 && (
                        <div>
                          <p className="text-[10px] tracking-normal text-muted-foreground mb-1">Attempts</p>
                          <ul className="text-[11px] font-mono text-foreground space-y-0.5">
                            {attemptsOf(r).map((a) => (
                              <li key={a.attempt}>
                                #{a.attempt} {a.model} · {a.status === 200 ? "ok" : a.status === "AbortError" ? "timed out (our limit)" : String(a.status)} · {formatMs(a.ms)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {r.error && (
                        <div className={cn("text-xs", isSkipped(r) ? "text-muted-foreground" : "text-destructive")}>
                          <p className="font-medium mb-1">{isSkipped(r) ? "Skipped" : "Error"}</p>
                          <pre className="whitespace-pre-wrap break-words font-mono text-[11px]">
                            {r.error}
                          </pre>
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] tracking-normal text-muted-foreground mb-1">
                          Input
                        </p>
                        <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-foreground bg-muted/50 p-2 max-h-72 overflow-auto">
                          {inputAsText(r.input_messages)}
                        </pre>
                      </div>
                      {r.output_text && (
                        <div>
                          <p className="text-[10px] tracking-normal text-muted-foreground mb-1">
                            Output
                          </p>
                          <pre
                            className={cn(
                              "whitespace-pre-wrap break-words font-mono text-[11px] text-foreground bg-muted/50 p-2 max-h-72 overflow-auto",
                            )}
                          >
                            {r.output_text}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
